import { useState } from 'react';
import type { TableFilterItem } from '../types';

interface UseBadgeFilterOptions {
  column: any;
  filterItems: TableFilterItem[];
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  badgeLabel?: string;
  filterLabel?: string;
}

/**
 * Generic hook for filtering items by badge
 *
 * Use cases:
 * - SPOT shipments: badgeLabel='SPOT', filterLabel='Only Show SPOT'
 * - Urgent items: badgeLabel='URGENT', filterLabel='Only Show Urgent'
 * - Any custom badge
 *
 * Features:
 * - Filter selected values to only items with specified badge
 * - LocalStorage persistence of filter state
 *
 * Note: Consumer is responsible for rendering the UI using the returned state
 *
 * @param column - Table column (for storage key)
 * @param filterItems - Available filter items
 * @param selectedValues - Currently selected values
 * @param setSelectedValues - Function to update selected values
 * @param badgeLabel - Badge label to filter by (default: 'SPOT')
 * @param filterLabel - Label for filter UI (default: 'Only Show SPOT')
 */
export const useBadgeFilter = ({
  column,
  filterItems,
  selectedValues,
  setSelectedValues,
  badgeLabel = 'SPOT',
  filterLabel = 'Only Show SPOT',
}: UseBadgeFilterOptions) => {
  const [onlyShowBadge, setOnlyShowBadge] = useState(false);
  const [wasApplied, setWasApplied] = useState(false);
  const storageKey = `badge-filter-${badgeLabel.toLowerCase()}-${column.id}`;

  /**
   * Handle filter apply
   * If badge filter is active, filter selectedValues to only include items with the badge
   */
  const handleApply = (onFilter: (id: string, values: string[]) => void) => {
    let valuesToApply = selectedValues;

    if (onlyShowBadge) {
      // Filter to only items with the specified badge
      const badgeItemIds = new Set(filterItems.filter((item) => item.badge?.label === badgeLabel).map((item) => item.id));
      valuesToApply = selectedValues.filter((value) => badgeItemIds.has(value));
      setSelectedValues(valuesToApply);

      // Save to localStorage
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          enabled: true,
          selectedIds: valuesToApply,
        })
      );
      setWasApplied(true);
    } else {
      // Clear localStorage if not using badge filter
      localStorage.removeItem(storageKey);
      setWasApplied(false);
    }

    onFilter(column.id, valuesToApply);
    return valuesToApply;
  };

  return {
    onlyShowBadge,
    setOnlyShowBadge,
    wasApplied,
    handleApply,
    badgeLabel,
    filterLabel,
  };
};
